import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, MapPin } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Hero = () => {
  const { language } = useLanguage();

  const texts = {
    it: {
      badge: "Guide di Viaggio Premium",
      title: "Viaggia",
      titleAccent: "come un local",
      description: "Guide curate da esperti con itinerari dettagliati, consigli insider e mappe interattive. Tutto ciò che serve per un viaggio indimenticabile.",
      cta: "Esplora le Guide",
      ctaSecondary: "Anteprima Gratuita",
      stats: [
        { value: "487+", label: "Viaggiatori" },
        { value: "4.9", label: "Valutazione" },
        { value: "4", label: "Destinazioni" }
      ]
    },
    en: {
      badge: "Premium Travel Guides",
      title: "Travel",
      titleAccent: "like a local",
      description: "Expert-curated guides with detailed itineraries, insider tips and interactive maps. Everything you need for an unforgettable trip.",
      cta: "Explore Guides",
      ctaSecondary: "Free Preview",
      stats: [
        { value: "487+", label: "Travelers" },
        { value: "4.9", label: "Rating" },
        { value: "4", label: "Destinations" }
      ]
    }
  };

  const t = texts[language];

  return (
    <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
      {/* Full background image */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80"
          alt="Mountain landscape"
          className="w-full h-full object-cover"
        />
        {/* Dark overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
      </div>
      
      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-24">
        <div className="max-w-3xl">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white px-4 py-2 rounded-full text-sm font-medium mb-8"
          >
            <MapPin className="w-4 h-4 text-[#C9B896]" />
            {t.badge}
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight mb-6"
          >
            <span className="text-white">{t.title}</span>
            <br />
            <span className="bg-gradient-to-r from-[#C9B896] to-[#D4C4A8] bg-clip-text text-transparent">
              {t.titleAccent}
            </span>
          </motion.h1>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg sm:text-xl text-white/80 mb-10 max-w-2xl leading-relaxed"
          >
            {t.description}
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-wrap gap-4 mb-16"
          >
            <Link
              to="/guides"
              className="group bg-white text-black hover:bg-[#f0f0f0] px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 flex items-center gap-2"
              data-testid="hero-cta-guides"
            >
              {t.cta}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/preview/iceland"
              className="text-white hover:text-[#C9B896] px-8 py-4 rounded-full font-medium text-lg transition-colors border border-white/30 hover:border-[#C9B896]/50 backdrop-blur-sm"
              data-testid="hero-cta-preview"
            >
              {t.ctaSecondary}
            </Link>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex gap-12"
          >
            {t.stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl font-bold text-white flex items-center justify-center gap-1">
                  {stat.value}
                  {i === 1 && <Star className="w-5 h-5 text-[#C9B896] fill-[#C9B896]" />}
                </div>
                <div className="text-sm text-white/60 mt-1">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
